import { sum as d3Sum, median } from 'd3-array';
import colors from './color-scales';
import sortOrder from '../assets/data/keys.sorted.json';

const sum = (entries, accessor) => {
  const isAllNaN = entries.every((entry) => {
    const val = accessor(entry);
    return Number.isNaN(+val);
  });

  if (isAllNaN) {
    return NaN;
  }

  return d3Sum(entries, accessor);
};

const config = {
  indicators: [
    // in regenbogen reihenfolge
    {
      key: 'anteil_frauen_gehalt',
      label: 'Durchschnittsgehalt von Frauen im Vergleich zu Männern',
      shortLabel: 'Gender Pay Gap',
      description:
        'Kaum ein Merkmal macht die ostdeutschen Länder so sichtbar wie der Gehaltsabstand zwischen Männern und Frauen: Für Ostfrauen war und ist die volle Berufstätigkeit selbstverständlich. In 23 Kreisen und Städten verdienen sie sogar mehr - alle liegen im Osten, allen voran Cottbus und Frankfurt (Oder).',
      decimals: 1,
      unit: '%',
      source: 'Quelle: Bundesagentur für Arbeit, 2019',
      calculateValue: (entries) => {
        return (
          (sum(entries, (d) => d['Median_Arbeitsentgelt_Frauen']) /
            sum(entries, (d) => d['Median_Arbeitsentgelt_Maenner']) -
          1) * 100
        );
      },
    },

    {
      key: 'anteil_vaeter_elterngeld',
      label: 'Anteil der Väter, die Elterngeld beziehen',
      shortLabel: 'Väter in Eltern&shy;zeit',
      description:
        'Seit 2007 gibt es das Elterngeld. Ausgerechnet in Bayern nehmen es besonders viele Väter in Anspruch: bei jeder zweiten Geburt. Wird damit das Klischee vom dort vorherrschenden traditionellen Familienbild widerlegt? Nicht ganz. Denn bei der Bezugsdauer rangieren die bayerischen Papas weit unten.',
      decimals: 0,
      unit: '% von allen Geburten',
      source: 'Quelle: Statistisches Bundesamt (Destatis), 2020',
      calculateValue: (entries) => {
        return (
          (sum(entries, (d) => d['Anzahl_vaeter_elterngeld']) /
            sum(entries, (d) => d['Geburten_2017'])) *
          100
        );
      },
    },
    {
      key: 'Anteil_RadWege',
      label: 'Anteil der alltäglichen Wege mit dem Fahrrad',
      shortLabel: 'Fahrrad&shy;nutzung',
      description:
        'Fast jede*r Einwohner*in Deutschlands (0,9 pro Kopf) hat ein Fahrrad. Durchschnittlich wird es für jeden neunten (11 Prozent) aller täglichen Wege genutzt. Allerdings sehr unterschiedlich. Dabei verläuft die Grenze nicht nur zwischen Stadt und Land, sondern vor allem zwischen Nord und Süd.',
      decimals: 1,
      unit: '%',
      source: 'Quelle: Studie "Mobilität in Deutschland" MiD, 2017',
      calculateValue: (entries) => {
        return (
          (sum(
            entries,
            (d) => d['Anteil_RadWege'] * d['bevoelkerung_Insgesamt']
          ) /
            sum(entries, (d) => d['bevoelkerung_Insgesamt'])) *
          100
        );
      },
    },
    {
      key: 'einbrueche_pro_kopf',
      label: 'Einbrüche in Wohnungen pro 10.000 Einwohner*innen',
      shortLabel: 'Wohnungs&shy;einbrüche',
      description:
        'In Deutschland wird rund 240 Mal am Tag in Wohnungen eingebrochen. Dabei registriert die Polizei im Norden mehr als doppelt so viele Fälle pro Einwohner*in wie in den südlichen Kreisen. Einbruchshochburg war zuletzt Bremen. Dagegen ist es in ganz Bayern auffällig ruhig.',
      decimals: 1,
      unit: 'je 10.000 Einwohner*innen und Jahr',
      source: 'Quelle: Polizeiliche Kriminalstatistik des BKA, 2019',
      calculateValue: (entries) => {
        return (
          (sum(entries, (d) => d['Wohnungseinbruch_gesamt']) /
            sum(entries, (d) => d['bevoelkerung_Insgesamt'])) *
          10000
        );
      },
    },
    {
      key: 'graffitti_pro_kopf',
      label:
        'Anzeigen wegen Sachbeschädigung durch Graffiti',
      shortLabel: 'Graffiti',
      description:
        'Der ganze Osten scheint eine bunt blühende Graffiti-Landschaft zu sein. Hier werden zumindest von der Polizei doppelt so viele Fälle von Sachbeschädigung durch Sprayer registriert als im Westen. Graffiti-Hauptstadt ist aber gemessen daran kein üblicher Szene-Verdächtiger wie Berlin, sondern Koblenz.',
      decimals: 1,
      unit: 'Anzeigen je 10.000 Einwohner*innen',
      source: 'Quelle: Polizeiliche Kriminalstatistik des BKA, 2019',
      calculateValue: (entries) => {
        return (
          (sum(entries, (d) => d['Graffitti_gesamt']) /
            sum(entries, (d) => d['bevoelkerung_Insgesamt'])) *
          10000
        );
      },
    },
    {
      key: 'Kommunale_Schulden',
      label: 'Kommunale Schulden der Kernhaushalte pro Kopf',
      shortLabel: 'Kommunale Schulden',
      description:
        'Die Pro-Kopf-Schulden der Kommunen sind im Westen mehr als doppelt so hoch wie im Osten. Das liegt an klammen Kassen in NRW, Rheinland-Pfalz und im Saarland. So steht Pirmasens in der Südwestpfalz mit 9630 Euro pro Kopf knietief im Schuldenberg, während Dresden schuldenfrei ist.',
      decimals: 0,
      unit: '€ je Einwohner*in',
      source:
        'Quelle: BBSR / Statistische Ämter des Bundes und der Länder, 2017',
      calculateValue: (entries) => {
        return (
          sum(
            entries,
            (d) => d['Kommunale_Schulden'] * d['bevoelkerung_Insgesamt']
          ) / sum(entries.filter(d => d['Kommunale_Schulden'] > 0), (d) => d['bevoelkerung_Insgesamt'])
        );
      },
    },
    {
      key: 'Arbeitsvolumen',
      label: 'Jährlich geleistete Arbeitsstunden pro Beschäftigte*r',
      shortLabel: 'Arbeits&shy;pensum',
      description:
        'Von wegen faule Ossis: Erwerbstätige in Ostdeutschland arbeiten mehr als acht Tage länger als ihre Kolleg*innen im Westen - und verdienen immer noch weniger. Das höchste Arbeitspensum haben aber die Beschäftigten in der VW-Stadt Wolfsburg - und in Kusel im Pfälzer Bergland die meiste Freizeit.',
      decimals: 0,
      unit: 'Stunden im Jahr',
      source:
        'Quelle: BBSR / Volkswirtschaftliche Gesamtrechnung der Länder, 2017',
      calculateValue: (entries) => {
        return (
          sum(
            entries,
            (d) => d['Arbeitsvolumen'] * d['bevoelkerung_Insgesamt']
          ) / sum(entries, (d) => d['bevoelkerung_Insgesamt'])
        );
      },
    },
    // {
    //   key: 'Anteil_Pendler_300_km_und_mehr',
    //   label: 'Pendler*innen mit einem Arbeitsweg von 300 km und mehr',
    //   shortLabel: 'Pendler*innen 300km',
    //   description:
    //     'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: '% aller Beschäftigten',
    //   source: 'Quelle: BBSR / Bundesagentur für Arbeit, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       sum(
    //         entries,
    //         (d) =>
    //           d['Anteil_Pendler_300_km_und_mehr'] *
    //           d['Anzahl_SVbeschaeftigte_gesamt']
    //       ) / sum(entries, (d) => d['Anzahl_SVbeschaeftigte_gesamt'])
    //     );
    //   },
    // },
    // {
    //   key: 'Anteil_Pendler_50_km_und_mehr',
    //   label: 'Pendler*innen mit einem Arbeitsweg von 50 km und mehr',
    //   shortLabel: 'Pendler*innen 50km',
    //   description:
    //     'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: '% aller Beschäftigten',
    //   source: 'Quelle: BBSR / Bundesagentur für Arbeit, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       sum(
    //         entries,
    //         (d) =>
    //           d['Anteil_Pendler_50_km_und_mehr'] *
    //           d['Anzahl_SVbeschaeftigte_gesamt']
    //       ) / sum(entries, (d) => d['Anzahl_SVbeschaeftigte_gesamt'])
    //     );
    //   },
    // },
    {
      key: 'Anteil_Auspendler',
      label: 'Beschäftigte mit Arbeitswegen über die Gemeindegrenze hinaus',
      shortLabel: 'Pendler*innen',
      description:
        'Die Mehrheit aller Beschäftigten pendelt zur Arbeit - typischerweise von den Speckgürteln in die Großstädte, mit durchschnittlich knapp 17 Kilometern Arbeitsweg. Im Westen ist der Pendler*innen-Anteil insgesamt deutlich höher als in Ostdeutschland. Dort pendeln aber mehr Menschen auf Langstrecken.',
      decimals: 1,
      unit: '% aller Beschäftigten',
      source: 'Quelle: BBSR / Bundesagentur für Arbeit, 2017',
      calculateValue: (entries) => {
        return (
          sum(
            entries,
            (d) => d['Anteil_Auspendler'] * d['Anzahl_SVbeschaeftigte_gesamt']
          ) / sum(entries, (d) => d['Anzahl_SVbeschaeftigte_gesamt'])
        );
      },
    },
    {
      key: 'Pflegebeduerftige_pro_10000',
      label: 'Anteil Pflegebedürftigter an der Gesamtbevölkerung',
      shortLabel: 'Pflege&shy;bedürftigte',
      description:
        'Der Pflegenotstand ist eine Herausforderung für ganz Deutschland. 3,4 Millionen Menschen sind pflegebedürftig. Tendenz extrem steigend. Gemessen an der Bevölkerung werden schon jetzt besonders viele Pflegekräfte im “alten” Norden Brandenburgs gebraucht. In Freising (Bayern) ist der Bedarf am geringsten.',
      decimals: 1,
      unit: '% der Bevölkerung',
      source: 'Quelle: BBSR / Pflegestatistik des Bundes und der Länder, 2017',
      calculateValue: (entries) => {
        return (
          sum(
            entries,
            (d) =>
              d['Pflegebeduerftige_pro_10000'] * d['bevoelkerung_Insgesamt']
          ) /
          sum(entries, (d) => d['bevoelkerung_Insgesamt']) /
          100
        );
      },
    },
    {
      key: 'Stimmenanteile_Sonstige_Parteien',
      label:
        'Ergebnis für "Sonstige" (<&thinsp;5&thinsp;%) bei der Bundestagswahl',
      shortLabel: 'Klein&shy;parteien',
      description:
        'Beim Wahlverhalten sind sich Ostdeutsche und Bayern in einem Punkt einig: Sie stimmen deutlich häufiger für kleine Parteien, die bislang nicht den Sprung in den Bundestag geschafft haben. Die "Sonstigen" haben dort bei der letzten Wahl bis zu 12,6 Prozent (Kreis Landshut) ausgemacht.',
      decimals: 1,
      unit: '% bei der Bundestagswahl 2017',
      source: 'Quelle: Bundestagswahlstatistik, 2017',
      calculateValue: (entries) => {
        return (
          sum(
            entries,
            (d) =>
              d['Stimmenanteile_Sonstige_Parteien'] *
              d['bevoelkerung_Insgesamt']
          ) / sum(entries, (d) => d['bevoelkerung_Insgesamt'])
        );
      },
    },
    {
      key: 'Lebenserwartung',
      label: 'Durchschnittliche Lebenserwartung von Neugeborenen',
      shortLabel: 'Lebens&shy;erwartung',
      description:
        'Wer im Osten lebt, stirbt im Allgemeinen früher. Doch bei der aktuellen Lebenserwartung gibt es ein viel stärkeres Nord-Süd-Gefälle. Besonders gut alt werden lässt es sich zwischen Ammer- und Starnberger See in Bayern (ø 83,6 Jahre), während es im ärmeren Bremerhaven fast sechs Lebensjahre weniger sind.',
      decimals: 1,
      unit: 'Jahre',
      source:
        'Quelle: BBSR / Statistische Ämter des Bundes und der Länder, 2017',
      calculateValue: (entries) => {
        return (
          sum(
            entries,
            (d) => d['Lebenserwartung'] * d['bevoelkerung_Insgesamt']
          ) / sum(entries, (d) => d['bevoelkerung_Insgesamt'])
        );
      },
    },

    // {
    //   key: 'arbeitslosenquote',
    //   label: 'Arbeitslosenquote (Hartz IV)',
    //   shortLabel: 'Hartz IV-Arbeits&shy;losenquote',
    //   description:
    //     'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: '%',
    //   source: 'Quelle: Bundesagentur für Arbeit, 2019.',
    //   calculateValue: (entries) => {
    //     return (
    //       (sum(entries, (d) => d['Arbeitslose_SGBII_gesamt']) /
    //         sum(entries, (d) => d['Zivile_erwerbspersonen_gesamt'])) *
    //       100
    //     );
    //   },
    // },

    {
      key: 'kinderarmut',
      label: 'Anteil der Kinder unter 15 Jahren in Hartz-IV-Bedarfsgemeinschaften',
      shortLabel: 'Kinder in Hartz-IV',
      description:
        'Kein eigenes Zimmer, kein Taschengeld, keine Geräte fürs Homeschooling: Jedes achte Kind unter 15 Jahren wächst mit Hartz-IV auf und somit in Armut. Dabei verläuft die Armutsgrenze zwischen Nord und Süd. Besonders betroffen sind Kinder in Großstädten. In Gelsenkirchen (NRW) sind es rund 40 Prozent.',
      decimals: 1,
      unit: '% aller unter 15-jährigen',
      source: 'Quelle: Statistische Ämter des Bundes und der Länder, 2019',
      calculateValue: (entries) => {
        return (
          (sum(entries, (d) => d['Kinder_unter_15_in_SGBII']) /
            sum(
              entries.filter(d => d['Kinder_unter_15_in_SGBII'] > 0),
              (d) =>
                +d['bevoelkerung_unter_3_Jahre'] +
                +d['bevoelkerung_3_bis_unter_6_Jahre'] +
                +d['bevoelkerung_6_bis_unter_10_Jahre'] +
                +d['bevoelkerung_10_bis_unter_15_Jahre']
            )) *
          100
        );
      },
    },

    {
      key: 'Anteil_Kinder_Privatschulen',
      label: 'Anteil der Kinder, die Schulen eines freien Trägers besuchen',
      shortLabel: 'Kinder in Privat&shy;schulen',
      description:
        'Rund eine Million Kinder besuchen eine Privatschule, jede*r elfte Schüler*in. Besonders hoch ist der Anteil im Osten. Dort, wo freie Schulen nicht erlaubt waren, begann nach der Wiedervereinigung ein Aufholprozess. Zugleich halbierte sich seitdem die Zahl der öffentlichen allgemeinbildenden Schulen.',
      decimals: 1,
      unit: '% aller Schüler*innen',
      source: 'Quelle: Statistische Ämter des Bundes und der Länder, 2016',
      calculateValue: (entries) => {
        return (
          sum(
            entries,
            (d) =>
              d['Anteil_Kinder_Privatschulen'] *
              (d['bevoelkerung_6_bis_unter_10_Jahre'] +
                d['bevoelkerung_10_bis_unter_15_Jahre'] +
                d['bevoelkerung_15_bis_unter_18_Jahre'] +
                d['bevoelkerung_18_bis_unter_20_Jahre'])
          ) /
          sum(
            entries,
            (d) =>
              d['bevoelkerung_6_bis_unter_10_Jahre'] +
              d['bevoelkerung_10_bis_unter_15_Jahre'] +
              d['bevoelkerung_15_bis_unter_18_Jahre'] +
              d['bevoelkerung_18_bis_unter_20_Jahre']
          )
        );
      },
    },

    {
      key: 'schulabbrecher_innen',
      label: 'Anteil der Schulabgänger*innen ohne Abschluss',
      shortLabel: 'Schul&shy;abbrech&shy;er*innen',
      description:
        'Fast sieben Prozent verlassen die Schule ohne Abschluss. Sorgenkind ist Ostdeutschland. So ist es in Eisenach (Thüringen) jede*r sechste Schüler. Als Gründe gelten ein vergleichsweise hoher Anteil an Förderschulen mit höheren Abbruchquoten, aber insbesondere das soziale Umfeld und Perspektivlosigkeit.',
      decimals: 1,
      unit: '% aller Schulabgänger*innen',
      source: 'Quelle: Statistische Ämter des Bundes und der Länder, 2018',
      calculateValue: (entries) => {
        return (
          (sum(entries, (d) => d['ohne_Hauptschulabschluss']) /
            sum(entries, (d) => d['schulabgaenger_innen_total'])) *
          100
        );
      },
    },
    {
      key: 'abiturient_innen',
      label: 'Anteil der Schulabgänger*innen mit allgemeiner Hochschulreife',
      shortLabel: 'Abitur',
      description:
        'Jede*r Dritte verlässt die Schule mit einem Abiturzeugnis - etwas mehr im Osten als im Westen. Doch Abi ist nicht gleich Abi. Das bayerische gilt als anspruchsvoll. Weniger Schüler*innen machen es dort direkt. Besonders beliebt ist in Bayern die Realschule - für viele als attraktiver Umweg zur Hochschulreife.',
      decimals: 1,
      unit: '% aller Schulabgänger*innen',
      source: 'Quelle: Statistische Ämter des Bundes und der Länder, 2018',
      calculateValue: (entries) => {
        return (
          (sum(entries, (d) => d['mit_allgemeiner_Hochschulreife']) /
            sum(entries, (d) => d['schulabgaenger_innen_total'])) *
          100
        );
      },
    },

    {
      key: 'pkw_pro_kopf',
      label: 'Bestand zugelassene Pkw pro Kopf',
      shortLabel: 'Autos',
      description:
        'DDR-Bürger*innen mussten zehn Jahre nur auf einen Trabi warten. Entsprechend hoch war der Nachholbedarf bei Autos. Die Ost-Bundesländer grenzen sich aber noch immer ab. Die Karte macht zudem Standorte der deutschen Autobauer sichtbar - und zeigt Berlin als Hauptstadt des Autoverzichts.',
      decimals: 2,
      unit: 'Pkws pro Kopf',
      source: 'Quelle: Kraftfahrt-Bundesamt, 2019',
      calculateValue: (entries) => {
        return (
          sum(entries, (d) => d['PKW_gesamt']) /
          sum(entries, (d) => d['bevoelkerung_Insgesamt'])
        );
      },
    },
    {
      key: 'wohnmobile_pro_kopf',
      label: 'Bestand zugelassener Wohnmobile pro 10.000 Einwohner*innen',
      shortLabel: 'Wohn&shy;mobile',
      description:
        'Einfach losfahren, irgendwo halten, die Freiheit genießen: Urlaub mit dem Wohnmobil boomt - egal ob mit ausgebautem Bully oder Luxus-Eigenheim auf Rädern. In der Corona-Krise ist es der einzige Autotyp, der bei den Neuzulassungen zulegt. Doch beim Bestand ist es bislang ein West-Phänomen.',
      decimals: 1,
      unit: 'pro 10.000 Einwohner*innen',
      source: 'Quelle: Bundeskraftfahramt, 2019',
      calculateValue: (entries) => {
        return (
          (sum(entries, (d) => d['Wohnmobile_gesamt']) /
            sum(entries, (d) => d['bevoelkerung_Insgesamt'])) *
          10000
        );
      },
    },
    {
      key: 'anteil_haushalte_mit_100_Mbit_s',
      label: 'Verfügbarkeit von Breitbandinternet mit mindestens 100 Mbit/s',
      shortLabel: 'Schnelles Internet',
      description:
        'Die digitale Schere geht weit auseinander: In Großstädten kann fast jede*r Internet mit 100 Mbit bekommen - zumindest technisch. Homeoffice auf dem Land ist dagegen oft kaum möglich. Schlusslicht ist der Kreis Cochem-Zell (Rheinland-Pfalz). Dort gibt es nur für jeden dritten Haushalt solche Bandbreiten.',
      decimals: 1,
      unit: '% aller Haushalte',
      source: 'Quelle: Breitbandatlas des Bundes, 2020.',
      calculateValue: (entries) => {
        return (
          sum(
            entries,
            (d) =>
              d['anteil_haushalte_mit_100_Mbit_s'] * d['bevoelkerung_Insgesamt']
          ) / sum(entries, (d) => d['bevoelkerung_Insgesamt'])
        );
      },
    },

    {
      key: 'schweine_pro_kopf',
      label: 'Tiere in der Schweinehaltung pro Einwohner*in',
      shortLabel: 'Schweine',
      description:
        'Knapp 60 Kilogramm Fleisch verschlingen die Deutschen pro Kopf im Jahr, hauptsächlich Schwein. Es stammt aus immer größeren Mastbetrieben, auch für den Export. Der Kreis Vechta (Niedersachsen) ist seit Jahren die Toplage für die industrielle Schweinerei: Hier kommen auf jede* Bewohner*in neun Schweine.',
      decimals: 2,
      unit: 'je Einwohner*in',
      source: 'Quelle: Statistische Ämter des Bundes und der Länder, 2016',
      calculateValue: (entries) => {
        return (
          sum(entries, (d) => d['Schweine_gesamt']) /
          sum(entries.filter(d => d['Schweine_gesamt'] > 0), (d) => d['bevoelkerung_Insgesamt'])
        );
      },
    },
    // {
    //   key: 'Schweine_gesamt',
    //   label: 'Schweine gesamt',
    //   shortLabel: 'Schweine gesamt',
    //   description:
    //     'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Statistische Ämter des Bundes und der Länder, 2016.',
    //   calculateValue: (entries) => {
    //     return (
    //       (sum(entries, (d) => d['Schweine_gesamt']))
    //     );
    //   },
    // },
    // {
    //   key: 'schweine_pro_km2',
    //   label: 'Schweinedichte',
    //   shortLabel: 'Schweine&shy;dichte',
    //   description:
    //     'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: 'je km²',
    //   source: 'Quelle: Statistische Ämter des Bundes und der Länder, 2016.',
    //   calculateValue: (entries) => {
    //     return (
    //       (sum(entries, (d) => d['Schweine_gesamt']) /
    //         sum(entries, (d) => d['Bodenflaeche_gesamt'])) *
    //       1000
    //     );
    //   },
    // },
    // {
    //   key: 'olympia_pro_kopf',
    //   label: 'Olympia-Medaillengewinner*innen',
    //   shortLabel: 'Olympia&shy;gewinner*innen',
    //   description:
    //     'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: 'je 10.000 Einwohner*innen',
    //   source: 'Quelle: de.wikipedia.org, 2020.',
    //   calculateValue: (entries) => {
    //     return (
    //       (sum(entries, (d) => d['anzahl_olympia_medaillengewinner_innen']) /
    //         sum(entries, (d) => d['bevoelkerung_Insgesamt'])) *
    //       10000
    //     );
    //   },
    // },
    {
      key: 'anzahl_olympia_medaillengewinner_innen',
      label: 'Medaillengewinner*innen bei Olympischen Spielen nach Geburtsort',
      shortLabel: 'Olympia-Medaillen',
      description:
        'Deutschlands Olympioniken traten von 1968 bis 1988 getrennt an. Und die DDR buhlte um internationale Anerkennung durch Sport - mit allen Mitteln und großen Erfolgen. Im ewigen Medaillenspiegel steht “GDR” deutlich vor “FRG”, mit doppelt so vielen Medaillen und dreimal mehr Gold. Das wirkt nach.',
      decimals: 0,
      unit: 'Medaillengewinner*innen',
      source: 'Quelle: Ewiger Medaillenspiegel (IOC) / wikipedia.org, 2020',
      calculateValue: (entries) => {
        return sum(entries, (d) => d['anzahl_olympia_medaillengewinner_innen']);
      },
    },
    {
      key: 'auslaender_innen_anteil',
      label: 'Anteil Ausländer*innen an der Bevölkerung',
      shortLabel: 'Ausländer*innen',
      description:
        'Jede*r achte Einwohner*in Deutschlands hat keinen deutschen Pass. Und der Anteil steigt - vornehmlich durch Zuzug aus EU-Ländern. Am geringsten ist er, wo die Ressentiments gegen Ausländer*innen besonders hoch sind. So sind es im Erzgebirgskreis (Sachsen) nur zwei Prozent.',
      decimals: 1,
      unit: '% der Bevölkerung',
      source:
        'Quelle: Regionalatlas, Statistische Ämter des Bundes und der Länder, 2018',
      calculateValue: (entries) => {
        return (
          sum(
            entries,
            (d) => d['auslaender_innen_anteil'] * d['bevoelkerung_Insgesamt']
          ) / sum(entries, (d) => d['bevoelkerung_Insgesamt'])
        );
      },
    },

    {
      key: 'Durchschnittalter_der_Bevoelkerung',
      label: 'Durchschnittsalter der Bevölkerung',
      shortLabel: 'Durchschnitts&shy;alter',
      description:
        'Der historische Umbruch lässt den Osten bis heute alt aussehen: 1989 die Ausreisewelle aus der DDR - dann die große Abwanderung der 1990er-Jahre, Geburtenknick inklusive. Es waren die Jungen, die ihr Glück im Westen suchten. Die alten Bundesländer sind nun zwei Jahre jünger als die neuen.',
      decimals: 1,
      unit: 'Jahre',
      source:
        'Quelle: BBSR / Statistisches Bundesamt (Destatis), 2018 (Brandenburg: 2017)',
      calculateValue: (entries) => {
        return (
          sum(
            entries,
            (d) =>
              d['Durchschnittalter_der_Bevoelkerung'] *
              d['bevoelkerung_Insgesamt']
          ) / sum(entries, (d) => d['bevoelkerung_Insgesamt'])
        );
      },
    },

    {
      key: 'Anteil_Waldflaeche_an_Bodenflaeche_gesamt',
      label: 'Anteil der Waldgebiete an der Gesamtfläche',
      shortLabel: 'Wald',
      description:
        'Geliebt, genutzt - und nun vom Klimawandel bedroht: Für die Deutschen ist der Wald weitaus mehr als die Summe seiner Bäume. Immerhin ist knapp ein Drittel mit Waldflächen bedeckt. Suhl im Thüringer Wald ist Deutschlands waldreichste kreisfreie Stadt und toppt mit 66 Prozent auch sämtliche Landkreise.',
      decimals: 1,
      unit: '% der Gesamtfläche',
      source: 'Quelle: Statistische Ämter des Bundes und der Länder, 2018',
      calculateValue: (entries) => {
        return (
          sum(
            entries,
            (d) =>
              d['Anteil_Waldflaeche_an_Bodenflaeche_gesamt'] *
              d['Bodenflaeche_gesamt']
          ) / sum(entries, (d) => d['Bodenflaeche_gesamt'])
        );
      },
    },
    {
      key: 'Wohnflaeche_je_Einwohner_in_m2',
      label: 'Wohnfläche je Einwohner*in und m²',
      shortLabel: 'Wohn&shy;fläche',
      description:
        'Eigentlich hat heute jede*r mehr Platz zum Wohnen. Seit Mitte der 90er ist die Wohnfläche pro Kopf um zehn m² gestiegen. Doch die Schere zwischen Stadt und Land geht auseinander. War es damals noch die gleiche Fläche, so sind es nun fünf m² Unterschied im Durchschnitt. Relativ wird es also enger in Städten.',
      decimals: 2,
      unit: 'm² pro Einwohner*in',
      source:
        'Quelle: BBSR / Fortschreibung des Wohngebäude-und Wohnungsbestandes des Bundes und der Länder, 2018',
      calculateValue: (entries) => {
        return (
          sum(
            entries,
            (d) =>
              d['Wohnflaeche_je_Einwohner_in_m2'] * d['bevoelkerung_Insgesamt']
          ) / sum(entries, (d) => d['bevoelkerung_Insgesamt'])
        );
      },
    },
    {
      key: 'millionaere_pro_kopf',
      label: 'Einkommensmillionär*innen pro 100.000 Einwohner*innen',
      shortLabel: 'Millionär*innen',
      description:
        'Wie reich Deutschlands Reiche wirklich sind, bleibt unklar. Denn zum Vermögen gibt es mangels Steuer allenfalls Schätzungen. Handfeste Daten gibt es zum Einkommen. Und dabei werden die Millionen vor allem durch Gewerbe sowie Vermieten und Verpachten erzielt - mit deutlicher Ost-West-Teilung.',
      decimals: 2,
      unit: 'je 100.000 Einwohner*innen',
      source: 'Quelle: Statistische Ämter der Länder, 2016',
      calculateValue: (entries) => {
        return (
          (sum(entries, (d) => d['einkommensmillionaere_gesamt']) /
            sum(entries, (d) => d['bevoelkerung_Insgesamt'])) *
          100000
        );
      },
    },
    {
      key: 'durchschnittl_sonnenstunden',
      label: 'Durchschnittliche Sonnenstunden pro Tag',
      shortLabel: 'Sonnen&shy;stunden',
      description:
        'Zum Schluss das Wetter: Im Osten geht die Sonne auf, und sie scheint dort auch länger als im Durchschnitt der alten Bundesländer. Allerdings liegt das wirklich am Norden und Westen. Wie es der Sonnenlauf vermuten lässt, liegen die meisten sonnigen Kreise in Süddeutschland, Sonnenkönigin war 2019 Stuttgart.',
      decimals: 1,
      unit: 'h pro Tag',
      source: 'Quelle: Deutscher Wetterdienst (DWD), 2019',
      calculateValue: (entries) => {
        return median(entries, (d) => d['durchschnittl_sonnenstunden'] / 365);
      },
    },
    {
      key: 'Anzahl_Merkel',
      label: 'Anzahl Telefonbucheinträge mit dem Nachnamen Merkel',
      shortLabel: 'Merkel',
      description: 'Ostdeutsche spielen in der Politik ganz oben mit. Dafür steht wie kein anderer der Nachname Merkel. In Angela Merkels Heimat, der Uckermark, ist er seltener. Kein Wunder: Hat sie den Nachnamen doch vom ersten Ehemann, der stammt aus dem Vogtland (Sachsen). Und je südlicher, desto mehr Merkel.',
      decimals: 0,
      unit: '',
      source:
        'Quelle: Verein für Computergenealogie e.V. / Telefonbuch-CD, 1996',
      calculateValue: (entries) => {
        return sum(entries, (d) => d['Anzahl_Merkel']);
      },
    },
    // {
    //   key: 'anzahl_ufosichtungen',
    //   label: 'Vermeintliche UFO Sichtungen',
    //   shortLabel: 'UFO-&shy;Sichtungen',
    //   description: 'Lorem ipsum',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: UFO-Datenbank des GEP e.V., 2020',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['anzahl_ufosichtungen']);
    //   },
    // },
    {
      key: 'gebaute_orgeln_pro_kopf',
      label: 'Seit 1996 gebaute Orgeln pro 100.000 Einwohner*innen',
      shortLabel: 'Orgeln',
      description: 'Mit rund 50.000 Orgeln hat Deutschland die höchste Orgel-Dichte der Welt. Deutscher Orgelbau und Orgelmusik sind Immaterielles Kulturerbe der Menschheit. Und diese Kultur lebt: Seit Mitte der 90er-Jahre sind 1247 neue Orgeln dazu gekommen, jedoch kaum im atheistischen Osten.',
      decimals: 1,
      unit: 'pro 100.000 Einwohner*innen',
      source:
        'Quelle: Orgelspiegel der Gesellschaft der Orgelfreunde e.V., 2019',
      calculateValue: (entries) => {
        return (
          (sum(entries, (d) => d['gesamtzahl_gebaute_orgeln']) /
            sum(entries, (d) => d['bevoelkerung_Insgesamt'])) *
          100000
        );
      },
    },
    {
      key: 'brauereien_pro_kopf',
      label: 'Braustätten pro 100.000 Einwohner*innen',
      shortLabel: 'Brauer&shy;eien',
      description: 'Rund hundert Liter Bier trinken die Deutschen im Jahr pro Kopf, fünfmal mehr als Wein. Und das gärt in rund 1900 Braustätten, fast jede zweite davon liegt in Bayern. Hier gibt es noch die kleineren familiengeführten Brauereien. Dabei ragt die fränkische Bierstadt Bamberg mit ihrem umliegenden Land heraus.',
      decimals: 1,
      unit: 'pro 100.000 Einwohner*innen',
      source: 'Quelle: Good Beer Guide Germany / Steve Thomas, 2018',
      calculateValue: (entries) => {
        return (
          (sum(entries, (d) => d['anzahl_brauereien']) /
            sum(entries, (d) => d['bevoelkerung_Insgesamt'])) *
          100000
        );
      },
    },

    {
      key: 'minijobquote',
      label: 'Anteil der Erwerbsfähigen (15-65 Jahre) ausschließlich mit Minijob',
      shortLabel: 'Minijobs',
      description:
        'Minijobs mit maximal 450 Euro im Monat teilen das Land in Ost und West. Grund sind die Frauen. Sie gehen im Osten deutlich häufiger einer Vollzeitbeschäftigung nach. Vor allem Betriebe in der westdeutschen Provinz setzen auf Minijobs. Dort sind es bis zu 26,3 Prozent aller Erwerbsfähigen (Borken).',
      decimals: 1,
      unit: '% aller Erwerbsfähigen',
      source: 'Quelle: Bundesagentur für Arbeit, 2019',
      calculateValue: (entries) => {
        return (
          (sum(entries, (d) => d['Summe_nur_geringfuegig_beschaeftigte']) /
            sum(
              entries,
              (d) =>
                +d['bevoelkerung_15_bis_unter_18_Jahre'] +
                +d['bevoelkerung_18_bis_unter_20_Jahre'] +
                +d['bevoelkerung_20_bis_unter_25_Jahre'] +
                +d['bevoelkerung_25_bis_unter_30_Jahre'] +
                +d['bevoelkerung_30_bis_unter_35_Jahre'] +
                +d['bevoelkerung_35_bis_unter_40_Jahre'] +
                +d['bevoelkerung_40_bis_unter_45_Jahre'] +
                +d['bevoelkerung_45_bis_unter_50_Jahre'] +
                +d['bevoelkerung_50_bis_unter_55_Jahre'] +
                +d['bevoelkerung_55_bis_unter_60_Jahre'] +
                +d['bevoelkerung_60_bis_unter_65_Jahre']
            )) *
          100
        );
      },
    },
    // {
    //   key: 'minijobquote',
    //   label: 'Minijobquote 2',
    //   shortLabel: 'Minijob&shy;quote 2',
    //   description:
    //     'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: '% aller zivilen Erwerbspersonen',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       (sum(entries, (d) => d['Summe_nur_geringfuegig_beschaeftigte']) /
    //         sum(entries, (d) => d['Zivile_erwerbspersonen_gesamt'])) *
    //       100
    //     );
    //   },
    // },

    // {
    //   key: 'betreeungsquote',
    //   label: 'Betreuungsquote von Kindern unter 3 Jahren',
    //   shortLabel: 'Betreuungsquote',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 2,
    //   unit: '%',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       sum(
    //         entries,
    //         (d) => d['betreeungsquote'] * d['betreute_kinder_gesamt']
    //       ) / sum(entries, (d) => d['betreute_kinder_gesamt'])
    //     );
    //   },
    // },
    // {
    //   key: 'ganztagsbetreuungsquote',
    //   label: 'Ganztags-Betreuungsquote von Kindern unter 3 Jahren',
    //   shortLabel: 'Ganztags-Betreuungsquote',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 2,
    //   unit: '%',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       sum(
    //         entries,
    //         (d) =>
    //           d['ganztagsbetreuungsquote'] * d['ganztagsbetreute_kinder_gesamt']
    //       ) / sum(entries, (d) => d['ganztagsbetreute_kinder_gesamt'])
    //     );
    //   },
    // },
    // {
    //   key: 'Erholungsflaeche_in_m2_je_Einwohner',
    //   label: 'Naherholungsflächen',
    //   shortLabel: 'Naherholungsflächen',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: 'in m² pro Einwohner*in',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       sum(
    //         entries,
    //         (d) =>
    //           d['Erholungsflaeche_in_m2_je_Einwohner'] *
    //           d['bevoelkerung_Insgesamt']
    //       ) / sum(entries, (d) => d['bevoelkerung_Insgesamt'])
    //     );
    //   },
    // },
    // {
    //   key: 'einbuergerungen_gesamt',
    //   label: 'Anzahl Einbürgerungen',
    //   shortLabel: 'Einbürgerungen',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: 'je 1000 Einwohner*innen',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       (sum(entries, (d) => d['einbuergerungen_gesamt']) /
    //         sum(entries, (d) => d['bevoelkerung_Insgesamt'])) *
    //       1000
    //     );
    //   },
    // },
    // {
    //   key: 'anteil_haushalte_mit_1000_Mbit_s',
    //   label: 'Breitbandinternet mit mindestens 1000 Mbit/s',
    //   shortLabel: 'Internetgeschwindigkeit (1000 MBit/s)',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: '% aller Haushalte',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       sum(
    //         entries,
    //         (d) =>
    //           d['anteil_haushalte_mit_1000_Mbit_s'] *
    //           d['bevoelkerung_Insgesamt']
    //       ) / sum(entries, (d) => d['bevoelkerung_Insgesamt'])
    //     );
    //   },
    // },
    // {
    //   key: 'ehescheidungen_gesamt',
    //   label: 'Verhältnis von Ehescheidungen zu Eheschließungen',
    //   shortLabel: 'Ehescheidungen (Verhältnis)',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       sum(entries, (d) => d['ehescheidungen_gesamt']) /
    //       sum(entries, (d) => d['eheschliessungen_gesamt'])
    //     );
    //   },
    // },
    // {
    //   key: 'ehescheidungen_gesamt_2',
    //   label: 'Ehescheidungen pro Einwohner',
    //   shortLabel: 'Ehescheidungen (pro Kopf)',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: 'je 1000 Einwohner*innen',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       (sum(entries, (d) => d['ehescheidungen_gesamt']) /
    //         sum(entries, (d) => d['bevoelkerung_Insgesamt'])) *
    //       1000
    //     );
    //   },
    // },
    // {
    //   key: 'Anzahl_SVbeschaeftigte_handwerk',
    //   label: 'Im Handwerk Beschäftigte',
    //   shortLabel: 'Handwerk',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: '% aller sozialversicherungspflichtig beschäftigten',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       (sum(entries, (d) => d['Anzahl_SVbeschaeftigte_handwerk']) /
    //         sum(entries, (d) => d['Anzahl_SVbeschaeftigte_gesamt'])) *
    //       100
    //     );
    //   },
    // },
    // {
    //   key: 'hundesteuer_gesamt_eur',
    //   label: 'Einnahmen aus Hundesteuer',
    //   shortLabel: 'Hundesteuer',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: '€ pro Einwohner*in',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       sum(entries, (d) => d['hundesteuer_gesamt_eur']) /
    //       sum(entries, (d) => d['bevoelkerung_Insgesamt'])
    //     );
    //   },
    // },
    // {
    //   key: 'durchschnittl_verfuegbares_einkommen',
    //   label: 'Durchschnittlich verfügbares Haushaltseinkommen',
    //   shortLabel: 'Haushaltseinkommen',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '€ pro Jahr ',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       sum(
    //         entries,
    //         (d) =>
    //           d['durchschnittl_verfuegbares_einkommen'] *
    //           d['bevoelkerung_Insgesamt']
    //       ) / sum(entries, (d) => d['bevoelkerung_Insgesamt'])
    //     );
    //   },
    // },
    // {
    //   key: 'Arbeitslose_langzeit_gesamt',
    //   label: 'Langzeit-Arbeitslosenquote',
    //   shortLabel: 'Langzeit-Arbeitslosenquote',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: '%',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       (sum(entries, (d) => d['Arbeitslose_langzeit_gesamt']) /
    //         sum(entries, (d) => d['Zivile_erwerbspersonen_gesamt'])) *
    //       100
    //     );
    //   },
    // },
    // {
    //   key: 'Fahrraddiebstahl_gesamt',
    //   label: 'Fahrraddiebstähle',
    //   shortLabel: 'Fahrraddiebstähle',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: 'je 10.000 Einwohner*innen und Jahr',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       sum(entries, (d) => d['Fahrraddiebstahl_gesamt']) /
    //       sum(entries, (d) => d['bevoelkerung_Insgesamt']) * 10000
    //     );
    //   },
    // },
    // {
    //   key: 'Anteil_FussWege',
    //   label: 'Anteil Wege die per Fuß gegangen werden',
    //   shortLabel: 'Fußstrecken',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 2,
    //   unit: '%',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['Anteil_FussWege'] * d['bevoelkerung_Insgesamt']) / sum(entries, (d) => d['bevoelkerung_Insgesamt']);
    //   },
    // },
    // {
    //   key: 'Anteil_MIVMitfahrerWege',
    //   label: 'Anteil Wege die im Auto mitgefahren werden',
    //   shortLabel: 'Mitfahrwege',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 2,
    //   unit: '%',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['Anteil_MIVMitfahrerWege'] * d['bevoelkerung_Insgesamt']) / sum(entries, (d) => d['bevoelkerung_Insgesamt']);
    //   },
    // },
    // {
    //   key: 'Anteil_MIVFahrerWege',
    //   label: 'Anteil Wege die mit dem KfZ gefahren werden',
    //   shortLabel: 'KfZ-Strecken',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 2,
    //   unit: '%',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['Anteil_MIVFahrerWege'] * d['bevoelkerung_Insgesamt']) / sum(entries, (d) => d['bevoelkerung_Insgesamt']);
    //   },
    // },
    // {
    //   key: 'Anteil_OEVWege',
    //   label: 'Anteil Wege die mit öffentlichen Verkehrsmitteln gefahren werden',
    //   shortLabel: 'ÖV-Wege',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 2,
    //   unit: '%',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       sum(
    //         entries,
    //         (d) => d['Anteil_OEVWege'] * d['bevoelkerung_Insgesamt']
    //       ) / sum(entries, (d) => d['bevoelkerung_Insgesamt'])
    //     );
    //   },
    // },
    // {
    //   key: 'Geburten_2017',
    //   label: 'Bezugsgröße 2017 geborene Kinder',
    //   shortLabel: 'Bezugsgröße Kinder 2017',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['Geburten_2017']);
    //   },
    // },
    // {
    //   key: 'anzahl_vaeter_elterngeld_min2monate',
    //   label: 'Väter, die mindestens 2 Monate in Elternzeit waren',
    //   shortLabel: 'Längere Elternzeit Väter',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '% von allen Geburten',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       (sum(entries, (d) => d['anzahl_vaeter_elterngeld_min2monate']) /
    //         sum(entries, (d) => d['Geburten_2017'])) *
    //       100
    //     );
    //   },
    // },
    // {
    //   key: 'Median_Arbeitsentgelt_gesamt',
    //   label: 'Durchnittlicher Lohn (Median)',
    //   shortLabel: 'Lohn',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '€ pro Monat',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       sum(
    //         entries,
    //         (d) =>
    //           d['Median_Arbeitsentgelt_gesamt'] * d['bevoelkerung_Insgesamt']
    //       ) / sum(entries, (d) => d['bevoelkerung_Insgesamt'])
    //     );
    //   },
    // },
    // {
    //   key: 'Median_Arbeitsentgelt_Maenner',
    //   label: 'Bezugsgröße Lohn Männer (Median)',
    //   shortLabel: 'Bezugsgröße Lohn Männer',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '€ pro Monat',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['Median_Arbeitsentgelt_Maenner'] * d['bevoelkerung_Insgesamt']) / sum(entries, (d) => d['bevoelkerung_Insgesamt']);
    //   },
    // },
    // {
    //   key: 'Zivile_erwerbspersonen_gesamt',
    //   label: 'Bezugsgröße Erwerbspersonen',
    //   shortLabel: 'Bezugsgröße Erwerbspersonen',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['Zivile_erwerbspersonen_gesamt']);
    //   },
    // },
    // {
    //   key: 'Arbeitslose_gesamt',
    //   label: 'Arbeitslosenquote',
    //   shortLabel: 'Arbeitslosenquote',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: '%',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       (sum(entries, (d) => d['Arbeitslose_gesamt']) /
    //         sum(entries, (d) => d['Zivile_erwerbspersonen_gesamt'])) *
    //       100
    //     );
    //   },
    // },

    // {
    //   key: 'Arbeitslosenquote',
    //   label: 'Arbeitslosenquote',
    //   shortLabel: 'Arbeitslosenquote',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 2,
    //   unit: '€',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return median(entries, (d) => d['Arbeitslosenquote']);
    //   },
    // },
    // {
    //   key: 'Bauernhöfe',
    //   label: 'Bauernhöfe',
    //   shortLabel: 'Bauernhöfe',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '€',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['Einwohner']);
    //   },
    // },
    // {
    //   key: 'Bevölkerung_km2',
    //   label: 'Bevölkerungsdichte',
    //   shortLabel: 'Bevölkerungsdichte',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   unit: '€',

    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['Einwohner']);
    //   },
    // },
    // {
    //   key: 'Durchschnittseinkommen',
    //   label: 'Durchschnittseinkommen',
    //   shortLabel: 'Durchschnittseinkommen',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   unit: '€',

    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['Einwohner']);
    //   },
    // },
    // {
    //   key: 'Haushaltseinkommen',
    //   label: 'Haushaltseinkommen',
    //   shortLabel: 'Haushaltseinkommen',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   unit: '€',

    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['Einwohner']);
    //   },
    // },
    // {
    //   key: 'Einwohner',
    //   label: 'Einwohner',
    //   shortLabel: 'Einwohner',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   unit: '€',

    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['Einwohner']);
    //   },
    // },
    // {
    //   key: 'Männliche.Gymsiasten',
    //   label: 'Männliche Gymnasiasten',
    //   shortLabel: 'Männliche Gymnasiasten',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   unit: '€',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['Einwohner']);
    //   },
    // },

    // {
    //   key: 'bevoelkerung_unter_3_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen unter 3 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung unter 3 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_unter_3_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_3_bis_unter_6_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen 3-6 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung 3-6 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_3_bis_unter_6_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_6_bis_unter_10_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen 6-10 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung 6-10 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_6_bis_unter_10_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_10_bis_unter_15_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen 10-15 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung 10-15 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_10_bis_unter_15_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_15_bis_unter_18_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen 15-18 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung 15-18 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_15_bis_unter_18_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_18_bis_unter_20_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen 18-20 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung 18-20 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_18_bis_unter_20_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_20_bis_unter_25_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen 20-25 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung 20-25 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_20_bis_unter_25_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_25_bis_unter_30_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen 25-30 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung 25-30 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: '%',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_25_bis_unter_30_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_30_bis_unter_35_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen 30-35 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung 30-35 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_30_bis_unter_35_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_35_bis_unter_40_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen 35-40 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung 35-40 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_35_bis_unter_40_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_40_bis_unter_45_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen 40-45 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung 40-45 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_40_bis_unter_45_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_45_bis_unter_50_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen 45-50 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung 45-50 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_45_bis_unter_50_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_50_bis_unter_55_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen 50-55 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung 50-55 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_50_bis_unter_55_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_55_bis_unter_60_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen 55-60 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung 55-60 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_55_bis_unter_60_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_60_bis_unter_65_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen 60-65 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung 60-65 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_60_bis_unter_65_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_65_bis_unter_75_Jahre',
    //   label: 'Bezugsgröße Einwohner*innen 65-75 Jahre',
    //   shortLabel: 'Bezugsgröße Bevölkerung 65-75 Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_65_bis_unter_75_Jahre']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_75_Jahre_und_mehr',
    //   label: 'Bezugsgröße Einwohner*innen 75 Jahre und älter',
    //   shortLabel: 'Bezugsgröße Bevölkerung 75+ Jahre',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_75_Jahre_und_mehr']);
    //   },
    // },
    // {
    //   key: 'bevoelkerung_Insgesamt',
    //   label: 'Bezugsgröße Einwohner*innen',
    //   shortLabel: 'Bezugsgröße Bevölkerung',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 0,
    //   unit: '',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return sum(entries, (d) => d['bevoelkerung_Insgesamt']);
    //   },
    // },
    // {
    //   key: 'Wahlbeteiligung',
    //   label: 'Wahlbeteiligung bei der Bundestagswahl 2017',
    //   shortLabel: 'Wahlbeteiligung',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: '%',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       sum(
    //         entries,
    //         (d) => d['Wahlbeteiligung'] * d['bevoelkerung_Insgesamt']
    //       ) / sum(entries, (d) => d['bevoelkerung_Insgesamt'])
    //     );
    //   },
    // },
    // {
    //   key: 'AErzte_je__Einwohner',
    //   label: 'Anzahl Ärzt*innen pro 10000 Einwohner*innen',
    //   shortLabel: 'Ärzt*innen',
    //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    //   decimals: 1,
    //   unit: 'je 10.000 Einwohner*innen',
    //   source: 'Quelle: Zickzackzuck, 2017.',
    //   calculateValue: (entries) => {
    //     return (
    //       sum(
    //         entries,
    //         (d) => d['AErzte_je__Einwohner'] * d['bevoelkerung_Insgesamt']
    //       ) / sum(entries, (d) => d['bevoelkerung_Insgesamt'])
    //     );
    //   },
    // },
  ],
  mapLabels: [
    ['Kiel', 54.3247693917538, 10.1321621611515],
    ['Hamburg', 53.5474244407493, 10.0163791720979],
    ['Hannover', 52.3794695632923, 9.76939916053457],
    ['Bremen', 53.1191041484888, 8.78056770262592],
    ['Düsseldorf', 51.2354844478529, 6.80988604305779],
    ['Mainz', 49.9741913110234, 8.24127881830166],
    ['Stuttgart', 48.7745618093221, 9.17183918550448],
    ['München', 48.1531979502324, 11.5470287206366],
    ['Saarbrücken', 49.2397998421921, 7.00656997083035],
    ['Berlin', 52.5015145302873, 13.401632725897],
    // ['Potsdam', 52.4261467031104, 13.0284865129038],
    ['Schwerin', 53.6214925097069, 11.4163200100636],
    ['Dresden', 51.0664405362649, 13.7832177074077],
    ['Magdeburg', 52.1165299687041, 11.6411771519529],
    ['Erfurt', 50.9832391736729, 11.0199889741609],
  ],

  animationCoords: [
    [
      [10.953369140624998, 54.13025957879543],
      [10.7666015625, 52.994950270267985],
      [10.294189453125, 51.58389660297626],
      [10.1513671875, 50.52041218671901],
      [10.799560546875, 50.254229882306205],
      [13.546142578125, 49.83798245308484],
    ],
    [
      [6.053466796875, 48.93693495409401],
      [8.19580078125, 49.95828842806968],
      [8.909912109375, 50.62507306341435],
      [9.942626953125, 51.46085244645549],
      [11.282958984375, 52.10650519075632],
      [12.63427734375, 52.35547370875268],
      [14.117431640625, 52.53627304145948],
      [15.391845703125, 52.6030475337285],
    ],
    [
      [10.074462890625, 46.800059446787316],
      [10.12939453125, 47.99727386804474],
      [10.08544921875, 49.023461463214126],
      [10.689697265625, 50.14170587960567],
      [10.997314453125, 50.72254683363231],
      [11.689453125, 51.337475662965204],
      [12.205810546875, 52.234528294213646],
      [12.3486328125, 52.96187505907603],
      [12.919921874999998, 53.85900655610469],
      [13.55712890625, 54.15600109028493],
      [14.348144531249998, 54.482804559582554],
    ],
  ],
};

config.indicators = config.indicators
  .sort((a, b) =>
    sortOrder.indexOf(b.key) < 0
      ? 0 // leave unknown keys where they are
      : sortOrder.indexOf(a.key) - sortOrder.indexOf(b.key)
  )
  .map((indicator, i) => {
    indicator.staticMapSrc = `images/staticMaps/${indicator.key}.png`;
    // usually reverse scales but not these ones
    if (['anteil_frauen_gehalt'].includes(indicator.key)) {
      indicator.colors = (colors[i] || colors[0]).slice(0);
    } else {
      indicator.colors = (colors[i] || colors[0]).slice(0).reverse();
    }
    return indicator;
  });

export default config;
